import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ShoppingCart, Trash2, Send } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [quoteItems, setQuoteItems] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("quoteItems") || "[]");
    setQuoteItems(items);
  }, []);

  const updateQuantity = (index, newQuantity) => {
    const updated = [...quoteItems];
    // Allow empty/partial values during typing
    const parsed = parseInt(newQuantity);
    updated[index].quantity = isNaN(parsed) ? 1 : Math.max(1, parsed);
    updated[index].quantityInput = newQuantity; // Store raw input for display
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
  };

  const finalizeQuantity = (index) => {
    const updated = [...quoteItems];
    const parsed = parseInt(updated[index].quantityInput || updated[index].quantity);
    updated[index].quantity = isNaN(parsed) || parsed < 1 ? 1 : parsed;
    updated[index].quantityInput = String(updated[index].quantity);
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
  };

  const updatePricePerUnit = (index, newPrice) => {
    const updated = [...quoteItems];
    updated[index].price_per_unit = parseFloat(newPrice) || 0;
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
  };

  const updatePackagingCost = (index, newCost) => {
    const updated = [...quoteItems];
    updated[index].packaging_cost = parseFloat(newCost) || 0;
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
  };

  const updateNotes = (index, notes) => {
    const updated = [...quoteItems];
    updated[index].notes = notes;
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
  };

  const removeItem = (index) => {
    const updated = quoteItems.filter((_, idx) => idx !== index);
    setQuoteItems(updated);
    localStorage.setItem("quoteItems", JSON.stringify(updated));
    toast.success("Item removed from quote");
  };

  const handleSubmit = async () => {
    if (quoteItems.length === 0) {
      toast.error("Please add products to your quote request");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/quotes`, {
        products: quoteItems,
        additional_notes: additionalNotes
      });

      toast.success("Quote request submitted successfully!");
      localStorage.removeItem("quoteItems");
      navigate("/my-quotes");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="bg-muted/20 border-b">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="quote-builder-heading">
              Quote Request Builder
            </h1>
            <p className="text-lg text-muted-foreground mt-2">Review and submit your product selection for quotation</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            {quoteItems.length === 0 ? (
              <Card className="p-12 text-center">
                <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Products in Quote</h3>
                <p className="text-muted-foreground mb-6">Browse the catalog and add products to request a quote.</p>
                <Link to="/catalog">
                  <Button data-testid="browse-catalog-btn">Browse Catalog</Button>
                </Link>
              </Card>
            ) : (
              <div className="space-y-6">
                {quoteItems.map((item, index) => (
                  <Card key={index} className="p-6" data-testid={`quote-item-${index}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>
                          {item.product_name}
                        </h3>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-item-btn-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor={`quantity-${index}`}>Quantity</Label>
                        <Input
                          id={`quantity-${index}`}
                          type="number"
                          min="1"
                          value={item.quantityInput !== undefined ? item.quantityInput : item.quantity}
                          onChange={(e) => updateQuantity(index, e.target.value)}
                          onBlur={() => finalizeQuantity(index)}
                          className="mt-2"
                          data-testid={`quantity-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`price-${index}`}>Price per Unit (₹)</Label>
                        <Input
                          id={`price-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price_per_unit || ""}
                          onChange={(e) => updatePricePerUnit(index, e.target.value)}
                          className="mt-2"
                          placeholder="Enter price"
                          data-testid={`price-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`packaging-${index}`}>Packaging Cost (₹)</Label>
                        <Input
                          id={`packaging-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.packaging_cost || ""}
                          onChange={(e) => updatePackagingCost(index, e.target.value)}
                          className="mt-2"
                          placeholder="Enter cost"
                          data-testid={`packaging-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`notes-${index}`}>Notes (Optional)</Label>
                        <Input
                          id={`notes-${index}`}
                          placeholder="Special requirements..."
                          value={item.notes || ""}
                          onChange={(e) => updateNotes(index, e.target.value)}
                          className="mt-2"
                          data-testid={`notes-input-${index}`}
                        />
                      </div>
                    </div>

                    {/* Show calculated totals */}
                    {(item.price_per_unit > 0 || item.packaging_cost > 0) && (
                      <div className="mt-4 pt-4 border-t border-border/50 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Unit Price:</span>
                          <span className="ml-2 font-semibold">₹{(item.price_per_unit || 0).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Packaging/Unit:</span>
                          <span className="ml-2 font-semibold">₹{(item.packaging_cost || 0).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Unit Cost:</span>
                          <span className="ml-2 font-semibold">₹{((item.price_per_unit || 0) + (item.packaging_cost || 0)).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Line Total:</span>
                          <span className="ml-2 font-bold text-primary">
                            ₹{(((item.price_per_unit || 0) + (item.packaging_cost || 0)) * item.quantity).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}

                <Card className="p-6">
                  <Label htmlFor="additional-notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="additional-notes"
                    placeholder="Any additional requirements, delivery timelines, or special instructions..."
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    rows={4}
                    className="mt-2"
                    data-testid="additional-notes-textarea"
                  />
                </Card>

                <div className="flex justify-between items-center pt-6">
                  <Link to="/catalog">
                    <Button variant="outline" data-testid="continue-browsing-btn">Continue Browsing</Button>
                  </Link>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="gap-2"
                    data-testid="submit-quote-btn"
                  >
                    <Send className="h-5 w-5" />
                    {loading ? "Submitting..." : "Submit Quote Request"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}